import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { ALL_BRANCHES, getSelectedBranchId, setSelectedBranchId, cacheFallbackBranchId } from '../utils/branchStorage';

const BranchContext = createContext(null);

export function BranchProvider({ apiUrl, children }) {
  // พนักงาน/ผู้จัดการสาขา (ล็อกอินแอดมินด้วยบัญชีพนักงาน role: 'manager' หรือ 'employee') ถูกล็อก
  // ไว้สาขาเดียวเสมอ — ตั้งไว้ตอน login (ดู App.jsx handleLogin) ไม่มีการสลับสาขาให้ ไม่ต้องยิง
  // GET /api/branches (list ทุกสาขา) เลยด้วยซ้ำ เพราะ endpoint นั้นเป็นของเจ้าของร้านเท่านั้นแล้ว
  // (403 ถ้าเรียก) — เช็คแค่ว่ามีค่าตั้งไว้เลย ไม่เจาะจง 'manager' เพราะช่องนี้มีแค่สอง role ที่
  // ไม่ใช่เจ้าของร้านเข้ามาได้ เจ้าของร้านไม่เคยตั้งค่านี้อยู่แล้ว
  const staffBranchId = localStorage.getItem('adminRole')
    ? localStorage.getItem('adminManagerBranchId')
    : null;

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchIdState] = useState(
    staffBranchId || getSelectedBranchId()
  );
  const [loading, setLoading] = useState(true);

  const loadBranches = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');

      if (staffBranchId) {
        // อ่านได้แค่สาขาตัวเอง (GET /api/branches/:id) ไม่ใช่ list ทั้งเครือ
        const response = await fetchWithRetry(`${apiUrl}/api/branches/${staffBranchId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const result = await response.json();
        if (result.data) setBranches([result.data]);
        return;
      }

      const response = await fetchWithRetry(`${apiUrl}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      const list = result.data ?? [];
      setBranches(list);
      if (list.length > 0) cacheFallbackBranchId(list[0].id);

      // ถ้าค่าที่เคยเลือกไว้ใช้ไม่ได้แล้ว (สาขาถูกปิด/ยังไม่เคยเลือก) และมีสาขาเดียว
      // ให้เลือกให้อัตโนมัติ ไม่ต้องบังคับผู้ใช้กดเลือกทั้งที่มีตัวเลือกเดียว
      const current = getSelectedBranchId();
      const stillValid = current === ALL_BRANCHES || list.some(b => b.id === current);
      if (!stillValid) {
        const next = list.length === 1 ? list[0].id : ALL_BRANCHES;
        setSelectedBranchId(next);
        setSelectedBranchIdState(next);
      }
    } catch (err) {
      console.error('[BranchContext] Failed to load branches:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, staffBranchId]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  // เปลี่ยนสาขาแล้ว reload ทั้งหน้า — ทุกหน้าตอนนี้ดึง branchId จาก localStorage
  // เอาตอน mount เอง ไม่ได้ subscribe context แบบ reactive ทั้งหมด รีโหลดคือทาง
  // ที่ชัวร์ที่สุดว่าทุกหน้าเห็นข้อมูลของสาขาที่เพิ่งเลือกจริง ๆ
  const selectBranch = useCallback((id) => {
    if (staffBranchId) return; // ผู้จัดการสลับสาขาไม่ได้
    setSelectedBranchId(id);
    window.location.reload();
  }, [staffBranchId]);

  const isAllBranches = !staffBranchId && selectedBranchId === ALL_BRANCHES;
  const effectiveBranchId = staffBranchId || (isAllBranches ? (branches[0]?.id ?? null) : selectedBranchId);
  const currentBranch = branches.find(b => b.id === effectiveBranchId) ?? null;

  return (
    <BranchContext.Provider value={{
      branches,
      loading,
      selectedBranchId: staffBranchId || selectedBranchId,
      isAllBranches,
      effectiveBranchId,
      currentBranch,
      // เจ้าของร้าน = ไม่ได้ถูกล็อกไว้สาขาเดียว ใช้ซ่อนงานข้ามสาขาจากผู้จัดการ
      // เป็นการซ่อนเพื่อไม่ให้สับสนเท่านั้น ตัวกันจริงอยู่ที่ requireOwnerAdmin ฝั่ง API
      isOwner: !staffBranchId,
      selectBranch
    }}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error('useBranch ต้องถูกเรียกภายใน BranchProvider เท่านั้น');
  return ctx;
}
